import type { ComponentType } from 'react';
import type { WorkbookRun } from '@erp/shared-types';
import {
  integrationsAt,
  type IntegrationsWith,
  type WorkbookIntegrationId,
  type WorkbookIntegrationSurface,
} from '@erp/workbooks';
import type { WorkbookEditor } from '../useWorkbookEditor';
import { WorkbookWorkflowsSection } from './WorkbookWorkflowsSection';
import { WorkbookTasksSection } from './WorkbookTasksSection';
import { WorkbookAgentsSection } from './WorkbookAgentsSection';
import { AskAgentAboutRun } from './AskAgentAboutRun';
import { RunTasks } from './RunTasks';
import { CreateStepTask } from './CreateStepTask';

export type AutomationsSectionProps = {
  editor: Pick<WorkbookEditor, 'workbookId' | 'definition' | 'edits'>;
  canUpdate: boolean;
};
export type RunActionProps = {
  editor: Pick<WorkbookEditor, 'workbookId' | 'definition'> & {
    runs: Pick<WorkbookEditor['runs'], 'data'>;
  };
  run: WorkbookRun;
};
export type ProcessStepActionProps = {
  workbookId: string;
  stepId: string;
  disabled: boolean;
};

/**
 * The frontend side of the Workbook Integration registry: for each surface an
 * integration declares in `@erp/workbooks`, the component it renders there.
 * Typed by the declaration, so a surface can't be declared and left
 * unimplemented, or implemented without being declared.
 */
const surfaces: {
  automations: Record<
    IntegrationsWith<'automations'>,
    ComponentType<AutomationsSectionProps>
  >;
  'run-actions': Record<
    IntegrationsWith<'run-actions'>,
    ComponentType<RunActionProps>
  >;
  'process-step': Record<
    IntegrationsWith<'process-step'>,
    ComponentType<ProcessStepActionProps>
  >;
} = {
  automations: {
    workflows: WorkbookWorkflowsSection,
    tasks: WorkbookTasksSection,
    agents: WorkbookAgentsSection,
  },
  'run-actions': { tasks: RunTasks, agents: AskAgentAboutRun },
  'process-step': { tasks: CreateStepTask },
};

/** The components at one surface, in the order the integrations are declared. */
function ordered<P>(
  surface: WorkbookIntegrationSurface,
  registry: Partial<Record<WorkbookIntegrationId, ComponentType<P>>>,
) {
  return integrationsAt(surface).flatMap((id) => {
    const Component = registry[id];
    return Component ? [{ id, Component }] : [];
  });
}

export function WorkbookAutomationSections(props: AutomationsSectionProps) {
  return ordered('automations', surfaces.automations).map(
    ({ id, Component }) => <Component key={id} {...props} />,
  );
}

export function WorkbookRunActions(props: RunActionProps) {
  return ordered('run-actions', surfaces['run-actions']).map(
    ({ id, Component }) => <Component key={id} {...props} />,
  );
}

export function WorkbookProcessStepActions(props: ProcessStepActionProps) {
  return ordered('process-step', surfaces['process-step']).map(
    ({ id, Component }) => <Component key={id} {...props} />,
  );
}
